import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, ArrowUpRight, Database, Zap, Shield, Key, Copy, Trash2, Code, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_CONFIG = {
  contact: { label: 'Contact Form', color: '#3b82f6', icon: '1' },
  consultation: { label: 'Consultations', color: '#8b5cf6', icon: '2' },
  holiday: { label: 'Holiday', color: '#ef4444', icon: '3' },
  letstalktaxes: { label: 'LetsTalkTaxes', color: '#f59e0b', icon: '4' },
  referral: { label: 'Referrals', color: '#10b981', icon: '5' },
  preregister: { label: 'Pre-Register', color: '#06b6d4', icon: '6' },
  coffee: { label: 'Coffee Friday', color: '#d97706', icon: '7' },
  ltt_kgob: { label: 'LTT-KGOB', color: '#ec4899', icon: '8' },
  ppc_ads: { label: 'PPC Ads', color: '#f97316', icon: '9' },
  business_health_check: { label: 'Business Health Check', color: '#14b8a6', icon: '10' },
  business_health_check_quote: { label: 'BHC Custom Quote', color: '#D4AF37', icon: '11' },
  penalty_recovery: { label: 'Penalty Recovery', color: '#0ea5e9', icon: '12' },
};

// Full API specs per source — embedded directly in the UI for copy-paste
const API_SPECS = {
  contact: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/contact',
    description: 'Receives qualified contact form leads (after email verification + scoring)',
    payload: `{
  "source": "kgob.com/contact",
  "kgob_lead_id": "uuid-string",
  "timestamp": "2026-02-22T14:30:00.000Z",
  "first_name": "John",
  "last_name": "Smith",
  "business_name": "Smith Consulting LLC",
  "email": "john.smith@example.com",
  "phone": "+17045551234",
  "email_verified": true,
  "verified_at": "2026-02-22T14:20:00.000Z",
  "consent": true,
  "audience": "prospective_client",
  "industry": "Real Estate",
  "annual_revenue": "1m_5m",
  "accounting_software": "QuickBooks Online",
  "services": ["tax", "advisory"],
  "timeline": "immediate",
  "values_fit": ["integrity", "excellence"],
  "story": "We're a growing real estate firm looking for a proactive CPA...",
  "referral_source": "google",
  "referrer_name": null,
  "scoring": {
    "score": 85,
    "grade": "A",
    "routing": "high_priority",
    "spam_flags": []
  },
  "geolocation": {
    "ip": "72.134.55.21",
    "country": "United States",
    "country_code": "US",
    "city": "Charlotte",
    "region": "North Carolina",
    "latitude": 35.2271,
    "longitude": -80.8431,
    "isp": "Spectrum",
    "timezone": "America/New_York",
    "vpn": false,
    "tor": false,
    "high_risk": false
  },
  "status": "qualified",
  "created_at": "2026-02-22T14:25:00.000Z"
}`,
    fields: [
      'audience: current_client | prospective_client | professional | other',
      'annual_revenue: under_500k | 500k_1m | 1m_5m | 5m_10m | over_10m',
      'services[]: tax | advisory | resolution | accounting | audit | valuation',
      'timeline: immediate | soon | planning | exploring',
      'scoring.grade: A | B | C | D',
    ],
  },
  consultation: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/consultation',
    description: 'Receives consultation bookings (new client, existing client, partner, other)',
    payload: `{
  "source": "kgob.com/book-consultation",
  "booking_source": "direct_booking",
  "kgob_consultation_id": "CONSULT-20260222-abc12345",
  "timestamp": "2026-02-22T15:00:00.000Z",
  "customer_type": "new_client",
  "customer_info": {
    "name": "Jane Doe",
    "first_name": "Jane",
    "last_name": "Doe",
    "email": "jane@company.com",
    "phone": "+17045559876",
    "company": "Doe Industries"
  },
  "details": {
    "services_needed": ["tax", "advisory"],
    "brief_description": "Need help with corporate tax strategy...",
    "annual_revenue": "5m_10m",
    "timeline": "immediate"
  },
  "scoring": {
    "score": 72,
    "grade": "B",
    "spam_flags": [],
    "potential_spam": false
  },
  "scheduling": {
    "scheduled_date": "2026-02-25",
    "scheduled_time": "10:00 AM",
    "timezone": "America/New_York",
    "zoom_link": "https://zoom.us/j/12345678"
  },
  "status": "scheduled",
  "admin_approval_required": false,
  "linked_lead_id": null,
  "created_at": "2026-02-22T15:00:00.000Z"
}`,
    fields: [
      'customer_type: new_client | existing_client | partner | other',
      'booking_source: direct_booking | contact_us_email_link | admin_created',
      'scoring: only present for new_client type',
      'scheduling: present once a time slot is booked',
    ],
  },
  holiday: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/holiday',
    description: 'Receives holiday campaign leads (Southern Christmas Show, QR codes)',
    payload: `{
  "source": "kgob.com/holiday",
  "kgob_lead_id": "uuid-string",
  "timestamp": "2026-02-22T16:00:00.000Z",
  "salutation": "Mr",
  "first_name": "Bob",
  "last_name": "Johnson",
  "email": "bob@example.com",
  "phone": "+17045551111",
  "interest_type": "Business Owner",
  "location": "Charlotte",
  "consent_marketing": true,
  "campaign": "Holiday",
  "channel": "QR (holiday-card)",
  "tags": ["holiday_2025", "money_saver_kit"],
  "pdf_downloaded": false,
  "created_at": "2026-02-22T16:00:00.000Z"
}`,
    fields: [
      'salutation: Mr | Mrs | Ms | Dr | null',
      'interest_type: Business Owner | Individual/Family | null',
      'location: Winston-Salem | Charlotte | Mooresville | Other | null',
    ],
  },
  letstalktaxes: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/letstalktaxes',
    description: 'Receives LetsTalkTaxes consultation bookings with coupon codes',
    payload: `{
  "source": "kgob.com/letstalktaxes",
  "kgob_booking_id": "uuid-string",
  "timestamp": "2026-02-22T17:00:00.000Z",
  "client_type": "business",
  "first_name": "Alice",
  "last_name": "Williams",
  "email": "alice@business.com",
  "phone": "+17045552222",
  "business_name": "Williams Corp",
  "email_verified": true,
  "coupon_code": "LTT-ABC123",
  "topic": "tax_planning",
  "topic_other_text": null,
  "main_question": "What's the best entity structure for my LLC?",
  "supporting_details": "Currently a single-member LLC...",
  "uploaded_documents": [
    {"file_id": "gridfs-abc123", "filename": "tax-return.pdf"}
  ],
  "appointment_date": "2026-02-28",
  "appointment_time": "2:00 PM",
  "appointment_timezone": "EST",
  "status": "pending_approval",
  "created_at": "2026-02-22T17:00:00.000Z"
}`,
    fields: [
      'client_type: individual | business',
      'topic: tax_planning | entity_structure | irs_resolution | bookkeeping | other',
      'status: pending_approval | approved | rejected',
    ],
  },
  referral: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/referral',
    description: 'Receives referral submissions (one API call per referee)',
    payload: `{
  "source": "kgob.com/refer-us",
  "kgob_referral_id": "uuid-string",
  "timestamp": "2026-02-22T18:00:00.000Z",
  "referrer": {
    "first_name": "Mike",
    "last_name": "Davis",
    "email": "mike@example.com",
    "phone": "+17045553333",
    "email_verified": true
  },
  "referee": {
    "first_name": "Sarah",
    "last_name": "Brown",
    "email": "sarah@example.com",
    "phone": "+17045554444"
  },
  "lead_type": "business_owner",
  "services_interested": ["tax", "accounting"],
  "personal_message": "Sarah, you should check out my CPA firm...",
  "email_sent_to_referee": true,
  "submission_id": "uuid-batch-id",
  "referral_number": 1,
  "total_in_batch": 2,
  "status": "new",
  "created_at": "2026-02-22T18:00:00.000Z"
}`,
    fields: [
      'lead_type: business_owner | individual | professional',
      'Up to 5 referrals per submission (each sent separately)',
      'submission_id groups referrals from the same batch',
    ],
  },
  preregister: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/preregister',
    description: 'Receives client portal pre-registration submissions',
    payload: `{
  "source": "kgob.com/pre-register",
  "kgob_prereg_id": "uuid-string",
  "timestamp": "2026-02-22T19:00:00.000Z",
  "client_type": "existing_client",
  "first_name": "Tom",
  "last_name": "Wilson",
  "phone": "+17045555555",
  "email": "tom@company.com",
  "business_name": "Wilson Enterprises",
  "email_verified": true,
  "status": "pending",
  "created_at": "2026-02-22T19:00:00.000Z"
}`,
    fields: [
      'client_type: existing_client | new_client | business',
    ],
  },
  coffee: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/coffee',
    description: 'Receives Coffee Friday walk-in lead captures',
    payload: `{
  "source": "kgob.com/coffee",
  "kgob_lead_id": "uuid-string",
  "timestamp": "2026-02-22T20:00:00.000Z",
  "first_name": "Lisa",
  "last_name": "Taylor",
  "email": "lisa@example.com",
  "location": "Charlotte",
  "opted_in": true,
  "event_date": "2026-02-22T00:00:00.000Z",
  "status": "new",
  "created_at": "2026-02-22T20:00:00.000Z"
}`,
    fields: [
      'location: Charlotte | Mooresville | Winston-Salem',
    ],
  },
  ltt_kgob: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/ltt-kgob',
    description: 'Receives Meet KGOB / LetsTalkTaxes PDF download leads',
    payload: `{
  "source": "meetKGOB-LetsTalktaxes",
  "kgob_lead_id": "uuid-string",
  "timestamp": "2026-02-22T21:00:00.000Z",
  "first_name": "David",
  "email": "david@example.com",
  "pdf_downloaded": false,
  "converted_to_purchase": false,
  "status": "new",
  "created_at": "2026-02-22T21:00:00.000Z"
}`,
    fields: [],
  },
  ppc_ads: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/ppc-ads',
    description: 'Receives PPC landing page leads from Google Ads campaigns (/lp/* pages). Default delivery is Bridge DB pull (kgob_crm_bridge.ppc_ads_leads); API push is optional.',
    payload: `{
  "source": "kgob.com/lp",
  "lp_slug": "tax-planning",
  "kgob_ppc_lead_id": "uuid-string",
  "timestamp": "2026-06-15T14:30:00.000Z",
  "client_type": "business",
  "first_name": "Mark",
  "last_name": "Anderson",
  "business_name": "Anderson Logistics LLC",
  "email": "mark@andersonlogistics.com",
  "phone": "+17045556789",
  "services_interested": ["tax_planning", "business_tax"],
  "brief_context": "We're a trucking company with 12 employees looking for a new CPA...",
  "consent": true,
  "lp_meta": {
    "landing_page_url": "https://kgob.com/lp/tax-planning",
    "thank_you_url": "https://kgob.com/lp/tax-planning/thank-you",
    "referrer": "https://www.google.com/",
    "utm_source": "google",
    "utm_medium": "cpc",
    "utm_campaign": "tax-planning-2026q1",
    "utm_term": "small business tax planning charlotte",
    "utm_content": "ad-variant-a",
    "gclid": "Cj0KCQjw...",
    "user_agent": "Mozilla/5.0..."
  },
  "geolocation": {
    "ip": "72.134.55.21",
    "country": "United States",
    "city": "Charlotte",
    "region": "North Carolina"
  },
  "status": "new",
  "created_at": "2026-06-15T14:30:00.000Z"
}`,
    fields: [
      'client_type: individual | business (business_name required when business)',
      'lp_slug: tax-planning | business-tax | accounting-bookkeeping | small-business-cpa | irs-tax-relief | irs-notice-help',
      'services_interested[] (1-6): tax_planning | business_tax | accounting_bookkeeping | small_business_cpa | irs_tax_relief | irs_notice_help',
      'kgob_ppc_lead_id: UNIQUE dedup key (UUID v4)',
      'lp_meta: full UTM + gclid attribution for Google Ads conversion tracking',
      'NOTE: email is format-validated but NOT code-verified (PPC conversion trade-off)',
      'Bridge collection: kgob_crm_bridge.ppc_ads_leads (default pull source)',
    ],
  },
  business_health_check: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/business-health-check',
    description: 'Receives PAID Mid-Year Business Health Check engagements ($4,995). Self-contained case file: contact, payment/refund, booking, client/team notes, and the full client-portal data (businesses, documents, messages, tickets, requests). Default delivery is Bridge DB pull (kgob_crm_bridge.business_health_check_leads); document bytes live in the shared bhc_documents GridFS bucket (fetch via documents[].gridfs_id).',
    payload: `{
  "source": "kgob.com/business-health-check",
  "kgob_bhc_engagement_id": "uuid-string",
  "timestamp": "2026-06-20T14:30:00.000Z",
  "variant": "business",
  "status": "scheduled",
  "fixed_fee": 4995,
  "first_name": "Mark",
  "last_name": "Anderson",
  "business_name": "Anderson Logistics LLC",
  "email": "mark@andersonlogistics.com",
  "phone": "+17045556789",
  "state": "NC",
  "role": "Owner",
  "intake": { "...": "qualifier answers" },
  "acceptance": { "...": "e-sign acknowledgements" },
  "payment": {
    "amount_display": "$4,995.00",
    "card_brand": "visa", "card_last4": "4242",
    "session_id": "cs_...", "payment_intent": "pi_...",
    "paid_at": "2026-06-18T10:00:00.000Z",
    "refunded": false, "refunded_at": null,
    "refund_id": null, "refunded_by": null
  },
  "booking": { "label": "...", "booking_ref": "...", "assigned_to": "..." },
  "client_notes": "Question for the call...", "client_notes_at": "...",
  "team_notes": "Reply from KGOB...", "team_notes_at": "...",
  "businesses": [ { "name": "...", "ein": "...", "entity_type": "..." } ],
  "documents": [ { "doc_id": "...", "filename": "2024-1120S.pdf", "year": "2024", "type": "tax_return", "gridfs_id": "..." } ],
  "messages": [ { "from": "me", "text": "...", "ts": "..." } ],
  "tickets": [ { "id": "...", "ref": "TKT-AB12CD", "subject": "...", "status": "Open", "thread": [ { "from": "me", "text": "...", "ts": "..." } ] } ],
  "requests": [ { "id": "...", "title": "...", "areas": [], "detail": "...", "status": "New" } ],
  "status": "scheduled",
  "created_at": "2026-06-18T09:55:00.000Z"
}`,
    fields: [
      'variant: business | trust',
      'status: paid | scheduled (only paid+ engagements sync)',
      'kgob_bhc_engagement_id: UNIQUE dedup key (UUID v4)',
      'payment.refunded / refund_id / refunded_by: Stripe is source of truth; either side may issue the refund',
      'documents[].gridfs_id: fetch file bytes from shared kgob_crm_bridge bhc_documents GridFS bucket',
      'messages[].from: "me" = client→team, "them" = KGOB team→client',
      'Bridge collection: kgob_crm_bridge.business_health_check_leads (default pull source)',
    ],
  },
  business_health_check_quote: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/business-health-check-quote',
    description: 'Receives out-of-scope Business Health Check custom-quote requests (prospect needs a tailored scope/price). Default delivery is Bridge DB pull (kgob_crm_bridge.business_health_check_quotes).',
    payload: `{
  "source": "kgob.com/business-health-check",
  "kgob_bhc_quote_id": "uuid-string",
  "timestamp": "2026-06-20T14:30:00.000Z",
  "first_name": "Dana",
  "last_name": "Reyes",
  "business_name": "Reyes Holdings LLC",
  "email": "dana@reyesholdings.com",
  "phone": "+17045551234",
  "state": "NC",
  "areas": ["multi_entity", "international"],
  "detail": "We have 3 entities + foreign subsidiary...",
  "timeline": "next_30_days",
  "contact_pref": "email",
  "intake_snapshot": { "...": "qualifier answers at time of request" },
  "engagement_id": null,
  "status": "new",
  "created_at": "2026-06-20T14:29:00.000Z"
}`,
    fields: [
      'kgob_bhc_quote_id: UNIQUE dedup key (UUID v4)',
      'areas[]: out-of-scope service areas the prospect selected',
      'timeline: prospect-stated urgency',
      'engagement_id: linked BHC engagement if the quote later converts (else null)',
      'Bridge collection: kgob_crm_bridge.business_health_check_quotes (default pull source)',
    ],
  },
  penalty_recovery: {
    endpoint: 'POST https://app.kgob.com/api/v1/crm/leads/penalty-recovery',
    description: 'COVID Penalty Recovery SESSION HANDOVER breadcrumb. kgob.com hosts only the public landing + free 2-minute check; payment, intake, login & payout all happen on app.kgob.com. This record is the session-keyed handover so app.kgob.com can reconcile the same visitor. Default delivery is Bridge DB pull (kgob_crm_bridge.penalty_recovery_leads).',
    payload: `{
  "source": "kgob.com/covid-penalty-recovery",
  "kgob_penalty_session_id": "uuid-string",
  "timestamp": "2026-06-20T14:30:00.000Z",
  "email": "owner@example.com",
  "eligible": true,
  "answers": { "had_employees": true, "ppp_overlap": false, "...": "..." },
  "wants_summary": true,
  "cta_events": [ { "target": "checkout", "at": "..." } ],
  "last_cta_target": "checkout",
  "first_seen_at": "2026-06-20T14:20:00.000Z",
  "lp_meta": { "utm_source": "google", "gclid": "..." },
  "geolocation": { "country": "United States", "city": "Charlotte" },
  "status": "handover",
  "created_at": "2026-06-20T14:20:00.000Z",
  "updated_at": "2026-06-20T14:30:00.000Z"
}`,
    fields: [
      'kgob_penalty_session_id: UNIQUE dedup key (session token minted on kgob.com)',
      'email / eligible / answers / wants_summary: free-check signal (present only if the visitor used the 2-min check)',
      'cta_events / last_cta_target: handover + attribution breadcrumbs as the visitor moves to app.kgob.com',
      'status: handover | checkout_complete (flipped when app.kgob.com confirms a paid checkout)',
      'Bridge collection: kgob_crm_bridge.penalty_recovery_leads (default pull source)',
    ],
  },
};

// Common auth + response spec (shared across all)
const COMMON_SPEC = `## Authentication (all endpoints)
Headers:
  Content-Type: application/json
  X-API-Key: {your_generated_api_key}
  X-Source-App: kgob.com
  X-Request-ID: {uuid}

## Expected Responses
Success (200/201):
  {"success": true, "crm_lead_id": "CRM-2026-000001"}

Duplicate (409 — treated as success):
  {"success": false, "error": "DUPLICATE", "existing_crm_lead_id": "CRM-2026-000001"}

Validation Error (422):
  {"success": false, "error": "VALIDATION_ERROR", "detail": "email: invalid"}

Auth Error (401):
  {"success": false, "error": "UNAUTHORIZED"}

## Idempotency
Use kgob_lead_id (or kgob_consultation_id etc.) as idempotency key.
kgob.com retries failed syncs up to 3x. Return 409 for duplicates.`;

function ApiSpecBlock({ source }) {
  const spec = API_SPECS[source];
  if (!spec) return null;

  const fullSpec = `# ${SOURCE_CONFIG[source]?.label} — API Spec for app.kgob.com
# ${spec.description}

## Endpoint
${spec.endpoint}

## Example Payload
${spec.payload}

${spec.fields.length > 0 ? `## Field Values\n${(spec.fields || []).map(f => `  - ${f}`).join('\n')}` : ''}

${COMMON_SPEC}`;

  const copySpec = () => {
    navigator.clipboard.writeText(fullSpec);
    toast.success('API spec copied to clipboard');
  };

  return (
    <div className="bg-[#060f1d] border border-gray-800 rounded-lg overflow-hidden" data-testid={`crm-spec-${source}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800/50 border-b border-gray-700">
        <span className="text-xs font-semibold text-gray-300 flex items-center gap-1.5">
          <Code className="w-3.5 h-3.5 text-[#D4AF37]" />
          API Spec for app.kgob.com
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-[11px] text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 px-2"
          onClick={copySpec}
          data-testid={`crm-copy-spec-${source}`}
        >
          <Copy className="w-3 h-3 mr-1" />
          Copy Full Spec
        </Button>
      </div>
      <div className="p-3 space-y-2 text-xs font-mono max-h-[280px] overflow-y-auto">
        <div>
          <span className="text-gray-500">Endpoint:</span>
          <span className="text-cyan-400 ml-1">{spec.endpoint}</span>
        </div>
        <div>
          <span className="text-gray-500">Auth Header:</span>
          <span className="text-yellow-400 ml-1">X-API-Key: {'{'} your_key {'}'}</span>
        </div>
        <div className="pt-1">
          <span className="text-gray-500">Payload:</span>
          <pre className="mt-1 text-emerald-400/90 whitespace-pre-wrap text-[11px] leading-relaxed">{spec.payload}</pre>
        </div>
        {spec.fields.length > 0 && (
          <div className="pt-1 border-t border-gray-800">
            <span className="text-gray-500">Field Values:</span>
            {(spec.fields || []).map((f, i) => (
              <div key={i} className="text-gray-400 ml-2 text-[11px]">{f}</div>
            ))}
          </div>
        )}
        <div className="pt-1 border-t border-gray-800">
          <span className="text-gray-500">Success Response:</span>
          <span className="text-emerald-400 ml-1">{`{"success": true, "crm_lead_id": "..."}`}</span>
        </div>
        <div>
          <span className="text-gray-500">Duplicate (409):</span>
          <span className="text-yellow-400 ml-1">{`{"success": false, "error": "DUPLICATE", "existing_crm_lead_id": "..."}`}</span>
        </div>
      </div>
    </div>
  );
}

export default function CRMSyncTab({ sessionToken }) {
  const [stats, setStats] = useState(null);
  const [bridge, setBridge] = useState(null);
  const [keys, setKeys] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [generating, setGenerating] = useState({});
  const [expandedSource, setExpandedSource] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [logStatusFilter, setLogStatusFilter] = useState(null);
  const [showSpec, setShowSpec] = useState({});
  const [newKeyDialog, setNewKeyDialog] = useState({ open: false, source: null, key: null });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${sessionToken}` };
      const [statsRes, keysRes, logsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/keys`, { headers }),
        fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/logs?limit=50${sourceFilter ? `&source=${sourceFilter}` : ''}`, { headers }),
      ]);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
        setBridge(statsData.bridge);
      }
      if (keysRes.ok) setKeys((await keysRes.json()).keys);
      if (logsRes.ok) setLogs((await logsRes.json()).logs);
    } catch (err) {
      console.error('Failed to fetch CRM sync data:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, sourceFilter]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleGenerateKey = async (source) => {
    setGenerating((prev) => ({ ...prev, [source]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/keys/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewKeyDialog({ open: true, source, key: data.api_key, label: data.label });
        toast.success(`API key generated for ${SOURCE_CONFIG[source]?.label}`);
        fetchData();
      } else {
        toast.error('Failed to generate key');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setGenerating((prev) => ({ ...prev, [source]: false }));
    }
  };

  const handleRevokeKey = async (source) => {
    if (!window.confirm(`Revoke API key for ${SOURCE_CONFIG[source]?.label}? Sync will stop for this source.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/keys/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (res.ok) {
        toast.success(`Key revoked for ${SOURCE_CONFIG[source]?.label}`);
        fetchData();
      }
    } catch {
      toast.error('Network error');
    }
  };

  const handleRetrySource = async (source) => {
    setRetrying((prev) => ({ ...prev, [source]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/admin/crm-sync/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      if (res.ok) {
        toast.success(`Retry triggered for ${SOURCE_CONFIG[source]?.label}`);
        setTimeout(fetchData, 3000);
      }
    } catch {
      toast.error('Network error');
    } finally {
      setRetrying((prev) => ({ ...prev, [source]: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="crm-sync-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-[#D4AF37] mr-3" />
        <span className="text-gray-400">Loading CRM sync data...</span>
      </div>
    );
  }

  const totals = stats
    ? Object.values(stats).reduce(
        (acc, s) => ({
          synced: acc.synced + (s.synced || 0),
          failed: acc.failed + (s.failed || 0) + (s.failed_permanent || 0),
          pending: acc.pending + (s.not_synced_yet || 0),
        }),
        { synced: 0, failed: 0, pending: 0 }
      )
    : { synced: 0, failed: 0, pending: 0 };

  const keysConfigured = keys ? Object.values(keys).filter(k => k.has_key).length : 0;

  const filteredLogs = logStatusFilter
    ? logs.filter(l => logStatusFilter === 'failed' ? (!l.synced && !l.skipped) : l.synced)
    : logs;

  return (
    <div className="space-y-6" data-testid="crm-sync-tab">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Database className="w-5 h-5 text-[#D4AF37]" />
            CRM Sync to app.kgob.com
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Real-time lead sync across all 12 sources
            {bridge?.connected && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-400">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                Bridge DB connected
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10" onClick={fetchData} data-testid="crm-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'SYNCED', value: totals.synced, icon: CheckCircle, color: 'emerald', borderColor: 'border-emerald-800/40' },
          { label: 'FAILED', value: totals.failed, icon: XCircle, color: 'red', borderColor: 'border-red-800/40' },
          { label: 'PENDING', value: totals.pending, icon: Clock, color: 'gray', borderColor: 'border-gray-700/40', note: 'Existing leads not yet synced' },
          { label: 'API KEYS', value: `${keysConfigured}/9`, icon: Shield, color: '[#D4AF37]', borderColor: 'border-[#D4AF37]/30' },
        ].map(card => (
          <Card key={card.label} className={`bg-[#07182F] border ${card.borderColor}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg bg-${card.color}-500/15 flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 text-${card.color}-400`} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">{card.label}</p>
                  <p className={`text-2xl font-bold text-${card.color}-400`}>{card.value}</p>
                  {card.note && <p className="text-[10px] text-gray-600 mt-0.5">{card.note}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-Source Grid */}
      <div>
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Sync by Source</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stats && Object.entries(stats).map(([source, data]) => {
            const config = SOURCE_CONFIG[source] || { label: source, color: '#6b7280' };
            const keyInfo = keys?.[source];
            const isExpanded = expandedSource === source;
            const total = (data.synced || 0) + (data.failed || 0) + (data.failed_permanent || 0) + (data.not_synced_yet || 0) + (data.skipped_no_key || 0);
            const syncRate = total > 0 ? Math.round(((data.synced || 0) / total) * 100) : 0;
            const hasKey = keyInfo?.has_key || data.has_key;
            const specVisible = showSpec[source];

            return (
              <Card key={source} className={`bg-[#07182F] border transition-all ${isExpanded ? 'border-[#D4AF37]/50 md:col-span-2' : 'border-gray-700/40 hover:border-gray-600/60'}`} data-testid={`crm-source-${source}`}>
                <button className="w-full text-left p-4" onClick={() => setExpandedSource(isExpanded ? null : source)} data-testid={`crm-source-toggle-${source}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold" style={{ backgroundColor: config.color + '33', color: config.color }}>{config.icon}</div>
                      <div>
                        <p className="font-semibold text-white text-sm">{config.label}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {hasKey ? (
                            <span className="flex items-center gap-1 text-xs text-emerald-400"><Zap className="w-3 h-3" /> Active</span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-yellow-500"><AlertTriangle className="w-3 h-3" /> No Key</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {total > 0 && (
                        <div className="flex items-center gap-2 text-xs">
                          {data.synced > 0 && <span className="text-emerald-400 font-medium">{data.synced}</span>}
                          {(data.failed || 0) + (data.failed_permanent || 0) > 0 && <span className="text-red-400 font-medium">{(data.failed || 0) + (data.failed_permanent || 0)} fail</span>}
                          {data.not_synced_yet > 0 && <span className="text-gray-500">{data.not_synced_yet} pending</span>}
                        </div>
                      )}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>
                  {total > 0 && (
                    <div className="mt-3 h-1 rounded-full bg-gray-800 overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${syncRate}%`, backgroundColor: syncRate === 100 ? '#10b981' : syncRate > 50 ? '#f59e0b' : '#6b7280' }} />
                    </div>
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-800 pt-3 space-y-3">
                    {/* Stats */}
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {[
                        { label: 'Synced', value: data.synced || 0, color: 'text-emerald-400' },
                        { label: 'Failed', value: data.failed || 0, color: 'text-red-400' },
                        { label: 'Permanent', value: data.failed_permanent || 0, color: 'text-red-300' },
                        { label: 'Skipped', value: data.skipped_no_key || 0, color: 'text-yellow-400' },
                        { label: 'Pending', value: data.not_synced_yet || 0, color: 'text-gray-400' },
                      ].map(item => (
                        <div key={item.label} className="bg-[#0B2240] rounded-md py-2">
                          <p className="text-[10px] text-gray-500 uppercase">{item.label}</p>
                          <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Bridge DB status */}
                    {bridge?.connected && bridge?.collections?.[source] && (
                      <div className="bg-[#0B2240] rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Database className="w-4 h-4 text-cyan-500" />
                          <span className="text-xs text-gray-400">Bridge DB:</span>
                          <span className="text-xs text-cyan-400 font-mono">{bridge.collections[source].collection}</span>
                        </div>
                        <span className="text-xs font-semibold text-cyan-400">
                          {bridge.collections[source].total || 0} leads
                        </span>
                      </div>
                    )}

                    {/* Key management */}
                    <div className="bg-[#0B2240] rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Key className="w-4 h-4 text-gray-500" />
                          <span className="text-xs text-gray-400">API Key:</span>
                          {keyInfo?.has_key ? (
                            <code className="text-xs text-emerald-400 font-mono bg-emerald-900/20 px-2 py-0.5 rounded">{keyInfo.masked_key}</code>
                          ) : (
                            <span className="text-xs text-gray-600">Not configured</span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {hasKey && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 px-2" onClick={(e) => { e.stopPropagation(); handleRevokeKey(source); }} data-testid={`crm-revoke-${source}`}>
                              <Trash2 className="w-3 h-3 mr-1" /> Revoke
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className={`h-7 text-xs px-3 ${hasKey ? 'border-gray-600 text-gray-400 hover:bg-gray-800' : 'border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10'}`} onClick={(e) => { e.stopPropagation(); handleGenerateKey(source); }} disabled={generating[source]} data-testid={`crm-generate-${source}`}>
                            {generating[source] ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Key className="w-3 h-3 mr-1" />}
                            {hasKey ? 'Regenerate' : 'Generate Key'}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Actions row */}
                    <div className="flex gap-2">
                      {hasKey && (data.failed || 0) > 0 && (
                        <Button variant="outline" size="sm" className="border-red-700/50 text-red-400 hover:bg-red-900/20 text-xs" onClick={(e) => { e.stopPropagation(); handleRetrySource(source); }} disabled={retrying[source]} data-testid={`crm-retry-${source}`}>
                          {retrying[source] ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Retry Failed
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="border-gray-600 text-gray-400 hover:bg-gray-800 text-xs" onClick={(e) => { e.stopPropagation(); setShowSpec(prev => ({ ...prev, [source]: !prev[source] })); }} data-testid={`crm-toggle-spec-${source}`}>
                        <FileText className="w-3 h-3 mr-1" />
                        {specVisible ? 'Hide' : 'View'} API Spec
                      </Button>
                    </div>

                    {/* Inline API Spec */}
                    {specVisible && <ApiSpecBlock source={source} />}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Sync Activity Log */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Sync Activity Log</h3>
          <div className="flex gap-2">
            <select className="bg-[#07182F] text-gray-400 text-xs border border-gray-700 rounded-md px-2 py-1.5" value={logStatusFilter || ''} onChange={(e) => setLogStatusFilter(e.target.value || null)} data-testid="crm-log-status-filter">
              <option value="">All Status</option>
              <option value="synced">Synced Only</option>
              <option value="failed">Failed Only</option>
            </select>
            <select className="bg-[#07182F] text-gray-400 text-xs border border-gray-700 rounded-md px-2 py-1.5" value={sourceFilter || ''} onChange={(e) => setSourceFilter(e.target.value || null)} data-testid="crm-log-filter">
              <option value="">All Sources</option>
              {Object.entries(SOURCE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>
        <Card className="bg-[#07182F] border border-gray-700/40">
          <CardContent className="p-0">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12" data-testid="crm-no-logs">
                <Database className="w-10 h-10 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{logs.length === 0 ? 'No sync activity yet' : 'No matching logs'}</p>
                <p className="text-gray-600 text-xs mt-1">Generate API keys above, then logs appear as leads sync</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800/50 max-h-[400px] overflow-y-auto">
                {(filteredLogs || []).map((log, idx) => {
                  const config = SOURCE_CONFIG[log.source] || { label: log.source };
                  return (
                    <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-[#0B2240]/50 transition-colors" data-testid={`crm-log-entry-${idx}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {log.synced ? <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" /> : (log.skipped ? <Clock className="w-4 h-4 text-amber-400 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />)}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{config.label}</span>
                            <code className="text-[11px] text-gray-600 font-mono truncate max-w-[120px]">{log.kgob_id}</code>
                          </div>
                          {log.error && !log.skipped && <p className="text-xs text-red-400/80 mt-0.5 truncate max-w-md">{log.error}</p>}
                          {log.skipped && <p className="text-xs text-amber-400/70 mt-0.5 truncate max-w-md">Push receiver not built on app.kgob.com — delivered via bridge</p>}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        {log.synced ? (
                          <Badge className="bg-emerald-900/40 text-emerald-400 border-emerald-700/30 text-[11px]">Synced</Badge>
                        ) : log.skipped ? (
                          <Badge className="bg-amber-900/40 text-amber-400 border-amber-700/30 text-[11px]">Skipped</Badge>
                        ) : (
                          <Badge className="bg-red-900/40 text-red-400 border-red-700/30 text-[11px]">Failed</Badge>
                        )}
                        {log.http_status && <span className="text-[10px] text-gray-600 ml-1">HTTP {log.http_status}</span>}
                        <p className="text-[11px] text-gray-600 mt-0.5">
                          {log.attempted_at ? new Date(log.attempted_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connection Info */}
      <Card className="bg-[#07182F] border border-gray-700/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>Push: <code className="text-gray-400 font-mono">app.kgob.com/api/v1/crm/leads/*</code></span>
            <span className="mx-1">|</span>
            <span>Bridge DB: <code className="text-gray-400 font-mono">kgob-crm-bridge</code> {bridge?.connected ? <span className="text-emerald-400">connected</span> : <span className="text-red-400">disconnected</span>}</span>
            <span className="mx-1">|</span>
            <span>Auto-refresh 30s</span>
            <span className="mx-1">|</span>
            <span>Failed syncs retry 3x</span>
            <span className="mx-1">|</span>
            <span>Pending = existing leads from before sync was built</span>
          </div>
        </CardContent>
      </Card>

      {/* New Key Dialog */}
      <Dialog open={newKeyDialog.open} onOpenChange={(open) => setNewKeyDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-[#0B2240] border-[#D4AF37]/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <Key className="w-5 h-5" /> API Key Generated
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Copy this key for <strong className="text-white">{newKeyDialog.label}</strong>. It won't be shown in full again.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-3">
            <div className="bg-[#07182F] border border-gray-700 rounded-lg p-3 flex items-center justify-between gap-2">
              <code className="text-xs text-emerald-400 font-mono break-all select-all" data-testid="crm-new-key-value">{newKeyDialog.key}</code>
              <Button variant="ghost" size="sm" className="shrink-0 text-gray-400 hover:text-white" onClick={() => copyToClipboard(newKeyDialog.key)} data-testid="crm-copy-key-btn">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-3">
              <p className="text-xs text-yellow-400">
                <strong>For app.kgob.com:</strong> Use this key in the <code className="bg-gray-800 px-1 rounded">X-API-Key</code> header. Click "View API Spec" on this source card for the full endpoint + payload format.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
